import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { hasSummaryPageExportRowSelection } from '../utility/table-selection-rows.util';
import { SelectionState } from './table-selection.service';
import { MessagePopupComponent } from '../components/message-popup/message-popup.component';
import {
  ActionIcons,
  actionText,
  lookupHeaders,
  ModalPopupMessages,
  pageType,
  popupButtons,
  popupWidth,
  getReportDownloadModalContent,
  StatusCodes,
  lookupcontrolNames,
} from '../utility/constants';
import {
  IDropdownResponse,
  IExcelTableData,
  SvgIconProp,
} from '../models/dynamic.model';
import { PrintPreviewPopupComponent } from '../components/print-preview-popup/print-preview-popup.component';
import { LookupPopupComponent } from '../components/lookup-popup/lookup-popup.component';
import { LookupColumnsConfig } from '../configs/common-configs';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ResetAllModals } from '../components/advanced-search/store/advance-search.action';
import { Router } from '@angular/router';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { SnakbarService } from './snakbar.service';
import { Store } from '@ngxs/store';
import { DownloadFileService } from './download-file.service';
import { MultiLookupPopupComponent } from '../components/multi-lookup-popup/multi-lookup-popup.component';
import { filterCustomsCityClearanceChannels } from '../utility/customs-city-clearance.util';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonService } from './common.service';
import {
  CustomsCityAccountClearanceConfig,
  CustomsCityValidateClearanceSelectionRequest,
  CustomsCityValidateClearanceSelectionResponse,
} from '../models/customs-city-clearance.model';
import { filterDownloadColumnsByVisibility } from '../utility/grid-column-preferences.util';
import { LocalStorageService } from './local-storage.service';

export interface SummaryPageExportPaging {
  totalCount: number;
  fromIndex: number;
  toIndex: number;
}

/** True when the grid page size covers the full search result set. */
export function allRecordsFitOnCurrentPage(
  paging: SummaryPageExportPaging
): boolean {
  const { totalCount, fromIndex, toIndex } = paging;
  if (totalCount <= 0) {
    return false;
  }
  const pageSize = toIndex - fromIndex;
  return pageSize > 0 && totalCount <= pageSize;
}

export function toSummaryExportPaging(
  totalCount: number,
  paging: { fromIndex: number; toIndex: number }
): SummaryPageExportPaging {
  return {
    totalCount,
    fromIndex: paging.fromIndex,
    toIndex: paging.toIndex,
  };
}

@Injectable({
  providedIn: 'root',
})
export class CommonPopupService {
  public fetchAdvanceSearchData = new BehaviorSubject<any>('');

  constructor(
    private readonly router: Router,
    private readonly snakbarService: SnakbarService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly fileService: DownloadFileService,
    private readonly store: Store,
    private readonly commonService: CommonService,
    private readonly localStorageService: LocalStorageService
  ) {}

  public openNoBtnWarningPopup(modalService: any, message: string) {
    modalService.openPopup(
      {
        message: message,
        isFooterRequired: false,
      },
      ModalPopupMessages.Warning,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
  }

  public openPodLinkedShipmentBlockedPopup(modalService: any): void {
    this.openPodValidationPopup(
      modalService,
      ModalPopupMessages.podLinkedShipmentError
    );
  }

  public openPodValidationPopup(modalService: any, message: string): void {
    modalService.openPopup(
      {
        message,
        isFooterRequired: false,
      },
      ModalPopupMessages.Validation,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
  }

  public openDownloadPopup(modalService: any, rangeOptions?: any[]) {
    const downloadDialogRef = modalService.openPopup(
      {
        isForDownload: true,
        isBulk: false,
        isFooterRequired: true,
        rangeOptions: rangeOptions || [],
      },
      ActionIcons.download,
      MessagePopupComponent,
      rangeOptions && rangeOptions.length > 0 ? popupWidth.searchPopup : popupWidth.downloadPopup,
    );
    return downloadDialogRef;
  }

  public openReportDownloadPopup(modalService: any, reportCode: string) {
    const copy = getReportDownloadModalContent(reportCode);
    return modalService.openPopup(
      {
        isForDownload: true,
        isBulk: false,
        isFooterRequired: true,
        rangeOptions: [],
        downloadInstructionMessage: copy.instruction,
        downloadInstructionNormalWeight: true,
        downloadConfirmButtonLabel: popupButtons.DownloadReport,
        downloadFormatOptions: [
          { value: 'xlsx', label: 'Excel' },
          { value: 'csv', label: 'CSV' },
        ],
      },
      copy.title,
      MessagePopupComponent,
      popupWidth.downloadPopup
    );
  }

  /** Invoice Date prompt shown before auto-rating; closes with `{ popupClose: true, invoiceDate: Date }`. */
  public openAutoRateInvoiceDatePopup(modalService: any) {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setMonth(minDate.getMonth() - 6);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 1);
    return modalService.openPopup(
      {
        message: ModalPopupMessages.autoRateInvoiceDatePrompt,
        isForInvoiceDatePrompt: true,
        defaultInvoiceDate: today,
        invoiceDateMin: minDate,
        invoiceDateMax: maxDate,
        isFooterRequired: true,
      },
      ModalPopupMessages.autoRateInvoiceDateTitle,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
  }

  public openArInvoiceDownloadPopup(modalService: any) {
    const downloadDialogRef = modalService.openPopup(
      {
        isForArInvoiceDownload: true,
        isBulk: false,
        isFooterRequired: true,
      },
      ModalPopupMessages.downloadArInvoiceTitle,
      MessagePopupComponent,
      popupWidth.downloadPopup
    );
    return downloadDialogRef;
  }

  public openPrintPopup(
    modalService: any,
    isFrom: string,
    columns: unknown,
    isRowSelected: boolean,
    isFromSummary: boolean,
    extraData?: any 
  ) {
    const downloadDialogRef = modalService.openPopup(
      {
        isFrom: isFrom,
        columns: columns,
        isRowSelected: isRowSelected,
        isFromSummary: isFromSummary,
        ...(extraData),
      },
      ActionIcons.Print,
      PrintPreviewPopupComponent,
      popupWidth.printPopup
    );
    return downloadDialogRef;
  }

  public openDeletePopup(modalService: any, popupConfig: any) {
    const downloadDialogRef = modalService.openPopup(
      {
        message: popupConfig.message,
        isMultiple: true,
        isFooterRequired: true,
        isBulk: popupConfig.isBulk,
      },
      popupConfig.messageType,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    return downloadDialogRef;
  }

  public openPrintWarningPopup(modalService: any) {
    const printDialogRef = modalService.openPopup(
      {
        message:
          ModalPopupMessages.printWarningContent +
          '. ' +
          ModalPopupMessages.printWarningContentConfirmation,
        isForPrintWarning: true,
        isFooterRequired: true,
      },
      ModalPopupMessages.Warning,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    return printDialogRef;
  }

  public openSummaryPageCurrentPageExportWarningPopup(
    modalService: any,
    action: 'download' | 'print'
  ) {
    const message =
      action === 'download'
        ? ModalPopupMessages.summaryPageDownloadCurrentPageWarning
        : ModalPopupMessages.summaryPagePrintCurrentPageWarning;

    return modalService.openPopup(
      {
        message,
        isFooterRequired: true,
        isMultiple: false,
        isForSummaryPageCurrentPageExportWarning: true,
      },
      ModalPopupMessages.Warning,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
  }

  /** No selection → warning popup unless all records fit on the current page. */
  public runSummaryPageExportAction(
    modalService: any,
    action: 'download' | 'print',
    hasRowSelection: boolean,
    destroyRef: DestroyRef,
    onProceed: () => void,
    exportPaging?: SummaryPageExportPaging,
    selectionState?: SelectionState
  ): void {
    const effectiveHasRowSelection = hasSummaryPageExportRowSelection(
      hasRowSelection,
      selectionState
    );
    if (
      effectiveHasRowSelection ||
      (exportPaging && allRecordsFitOnCurrentPage(exportPaging))
    ) {
      onProceed();
      return;
    }

    this.openSummaryPageCurrentPageExportWarningPopup(modalService, action)
      .afterClosed()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          onProceed();
        }
      });
  }

  public openLookupPopup(
    modalService: any,
    controlName: string,
    lookupData: IDropdownResponse[],
    selectedOption: string,
    excludedId?: number | string | null
  ) {
    const lookupColumns = LookupColumnsConfig.filter(
      columnsData => columnsData.label === this.getLookUpHeaderName(controlName)
    );
    const filteredLookupData =
      lookupcontrolNames.includes(controlName) && excludedId != null && excludedId !== ''
        ? lookupData.filter(item => String(item.id) !== String(excludedId))
        : lookupData;
    const lookupDialogRef = modalService.openPopup(
      {
        showModalCloseOption: true,
        displayedColumnsList: [
          ...lookupColumns.map(column => column.key),
          'action',
        ],
        tableColumns: lookupColumns,
        tableData:
          controlName === 'countryCode' ||
          controlName === 'state' ||
          lookupcontrolNames.includes(controlName)
            ? this.getLookupData(controlName, filteredLookupData)
            : filteredLookupData,
        selectedOptionData: selectedOption,
      },
      this.getLookUpHeaderName(controlName),
      LookupPopupComponent,
      popupWidth.searchPopup
    );
    return lookupDialogRef;
  }


  public openMultiLookupPopup(
    modalService: any,
    controlName: string,
    lookupData: IDropdownResponse[],
    selectedOption: string | IDropdownResponse[] = []
  ) {
    const lookupColumns = LookupColumnsConfig.filter(
      columnsData => columnsData.label === this.getLookUpHeaderName(controlName)
    );
    const lookupDialogRef = modalService.openPopup(
      {
        showModalCloseOption: true,
        displayedColumnsList: [
          ...lookupColumns.map(column => column.key),
          'action',
        ],
        tableColumns: lookupColumns,
        tableData:
          controlName === 'countryCode' ||
          controlName === 'state' ||
          controlName === 'destination' ||
          controlName === 'origin'
            ? this.getLookupData(controlName, lookupData)
            : lookupData,
        selectedOptionData: selectedOption,
      },
      this.getLookUpHeaderName(controlName),
      MultiLookupPopupComponent,
      popupWidth.searchPopup
    );
    return lookupDialogRef;
  }

  private getLookUpHeaderName(controlName: string) {
    if (controlName === 'countryCode') {
      return lookupHeaders.country;
    } else if (controlName === 'state') {
      return lookupHeaders.state;
    } else if (
      controlName === 'origin' ||
      controlName === 'originName' ||
      controlName === 'originID'
    ) {
      return lookupHeaders.origin;
    } else {
      return lookupHeaders.destination;
    }
  }

  private getLookupData(controlName: string, lookupData: IDropdownResponse[]) {
    if (controlName === 'countryCode') {
      return lookupData.map(data => {
        return {
          ...data,
          country: data.id + ' - ' + data.name,
          name: data.name,
        };
      });
    } else if (controlName === 'state') {
      return lookupData.map(data => {
        return {
          ...data,
          state: data.stateCode + ' - ' + data.name,
        };
      });
    } else if (lookupcontrolNames.includes(controlName)) {
      return lookupData.map(data => ({
        ...data,
        name: data.code ? `${data.code} - ${data.name}` : data.name,
      }));
    } else {
      return lookupData;
    }
  }

  public createErrorPopupData(
    modalService: any,
    message: string,
    title: string
  ) {
    modalService.openPopup(
      {
        message: message,
        isFooterRequired: false,
      },
      title,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
  }

  public openClearanceForCBPPopup(
    modalService: any,
    clearanceChannelType: IDropdownResponse[],
    isFromModule: string,
    selectedIds: number[],
    processClearancePayload?: CustomsCityValidateClearanceSelectionRequest,
    accountClearanceConfig?: CustomsCityAccountClearanceConfig
  ): MatDialogRef<MessagePopupComponent> | null {
    if (accountClearanceConfig?.noNeedClearance) {
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.noNeedClearanceForAccount,
        ModalPopupMessages.error
      );
      return null;
    }

    const customsCityChannels =
      filterCustomsCityClearanceChannels(clearanceChannelType);

    if (!customsCityChannels.length) {
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.clearanceChannelNotConfigured,
        ModalPopupMessages.error
      );
      return null;
    }

    if (accountClearanceConfig && !accountClearanceConfig.clearanceTypes.length) {
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.clearanceTypeNotConfigured,
        ModalPopupMessages.error
      );
      return null;
    }

    return modalService.openPopup(
      {
        clearanceType: customsCityChannels,
        clearanceTypeCodes: accountClearanceConfig?.clearanceTypes ?? [],
        configuredClearanceChannels:
          accountClearanceConfig?.clearanceChannels ?? [],
        isFrom: pageType.sendCBPClearance,
        isMultiple: false,
        isFooterRequired: true,
        isSendCBPClearance: true,
        isFromModule: isFromModule,
        selectedIds: selectedIds,
        processClearancePayload,
      },
      actionText.sendCbpClearance,
      MessagePopupComponent,
      popupWidth.searchPopup
    );
  }

  /** Refreshes parent screen when clearance popup closes after a successful submit. */
  public whenClearancePopupSucceeded(
    dialogRef: MatDialogRef<MessagePopupComponent>,
    onSuccess: () => void
  ): void {
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((popupData: unknown) => {
        if (popupData) {
          onSuccess();
        }
      });
  }

  public validateSelectionThenOpenClearancePopup(
    modalService: any,
    clearanceChannelType: IDropdownResponse[],
    isFromModule: string,
    selectedIds: number[],
    onSuccess: () => void
  ): void {
    this.commonService
      .validateClearanceSelection({
        ids: selectedIds,
        isFrom: isFromModule,
      })
      .pipe(take(1))
      .subscribe({
        next: resp =>
          this.handleClearanceSelectionValidation(
            modalService,
            clearanceChannelType,
            isFromModule,
            selectedIds,
            resp,
            onSuccess
          ),
        error: (error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
        },
      });
  }

  private handleClearanceSelectionValidation(
    modalService: any,
    clearanceChannelType: IDropdownResponse[],
    isFromModule: string,
    selectedIds: number[],
    resp: CustomsCityValidateClearanceSelectionResponse,
    onSuccess: () => void
  ): void {
    const processClearancePayload =
      resp?.data?.processClearancePayload ?? {
        ids: selectedIds,
        isFrom: isFromModule,
      };
    const isEligible =
      resp?.statusCode === StatusCodes.Success &&
      resp?.data?.outcome === 'eligible';
    const needsConfirmation =
      resp?.statusCode === StatusCodes.PartialAutoRating &&
      resp?.data?.requiresConfirmation === true;

    if (isEligible) {
      this.openValidatedClearancePopup(
        modalService,
        clearanceChannelType,
        isFromModule,
        processClearancePayload,
        onSuccess,
        resp?.data?.accountClearanceConfig
      );
      return;
    }

    if (needsConfirmation) {
      this.openClearanceSelectionConfirmation(
        modalService,
        clearanceChannelType,
        isFromModule,
        resp,
        onSuccess
      );
      return;
    }

    if (resp?.statusCode === StatusCodes.UnprocessableEntity) {
      this.snakbarService.openCustomisedSnackBar(
        resp.message,
        ModalPopupMessages.error
      );
      return;
    }

    this.errorHandlerService.handleApiError(resp);
  }

  private openClearanceSelectionConfirmation(
    modalService: any,
    clearanceChannelType: IDropdownResponse[],
    isFromModule: string,
    resp: CustomsCityValidateClearanceSelectionResponse,
    onSuccess: () => void
  ): void {
    const dialogRef = modalService.openPopup(
      {
        message: resp.message,
        isFooterRequired: true,
        isMultiple: false,
      },
      ModalPopupMessages.Warning,
      MessagePopupComponent,
      popupWidth.deletePopup
    );

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: unknown) => {
        if (result === true) {
          const processClearancePayload = resp?.data?.processClearancePayload;
          if (!processClearancePayload?.ids?.length) {
            return;
          }
          this.openValidatedClearancePopup(
            modalService,
            clearanceChannelType,
            isFromModule,
            processClearancePayload,
            onSuccess,
            resp?.data?.accountClearanceConfig
          );
        }
      });
  }

  private openValidatedClearancePopup(
    modalService: any,
    clearanceChannelType: IDropdownResponse[],
    isFromModule: string,
    processClearancePayload: CustomsCityValidateClearanceSelectionRequest,
    onSuccess: () => void,
    accountClearanceConfig?: CustomsCityAccountClearanceConfig
  ): void {
    if (!processClearancePayload.ids?.length) {
      return;
    }
    const dialogRef = this.openClearanceForCBPPopup(
      modalService,
      clearanceChannelType,
      isFromModule,
      processClearancePayload.ids,
      processClearancePayload,
      accountClearanceConfig
    );
    if (!dialogRef) {
      return;
    }
    this.whenClearancePopupSucceeded(dialogRef, onSuccess);
  }

  handleAdvanceSearch(
    searchObservable: Observable<any>,
    payload: any,
    navigateTo: string,
    isToaster: boolean,
    isAllSelected = false
  ): void {
    searchObservable.pipe(take(1)).subscribe({
      next: (data: any) => {
        if (
          data.status === StatusCodes.Success ||
          data.status === StatusCodes.SuccessfulRequest
        ) {
          if (data.status === StatusCodes.Success) {
            if (isToaster) {
              this.snakbarService.openCustomisedSnackBar(
                data.message,
                ModalPopupMessages.success
              );
            }
          }
          const fetchData = {
            payload: payload,
            response: data,
            isAllSelected: !!isAllSelected,
          };
          this.getAdvanceSearchData(fetchData);
          this.store.dispatch(new ResetAllModals());
          this.router.navigate([navigateTo]);
        } else if (data.status === StatusCodes.BadRequest) {
          this.errorHandlerService.handleApiError(data);
        } else {
          this.errorHandlerService.handleApiError(data);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorHandlerService.handleError(error);
      },
    });
  }

  public getAdvanceSearchData(data: any) {
    this.fetchAdvanceSearchData.next(data);
  }

  public setRequiredDataForDownloadFile(
    name: string,
    headers: string[],
    columns: string[],
    fileType: string,
    successMessage: string,
    data: any,
    storageKey?: string
  ) {
    let finalHeaders = headers;
    let finalColumns = columns;
    if (storageKey) {
      const filtered = filterDownloadColumnsByVisibility(
        this.localStorageService,
        storageKey,
        headers,
        columns
      );
      finalHeaders = filtered.headers;
      finalColumns = filtered.columnKeys;
    }
    const excelData: IExcelTableData = {
      name: name,
      headers: finalHeaders,
      headerKeys: finalColumns,
      tableData: data,
      selectedFileType: fileType,
      successMessage: successMessage,
      isFromSummary: true,
    };
    this.fileService.generateExcelCSVFile(excelData);
  }

  public setRequiredDataForDownload(
    name: string,
    headers: string[],
    columns: string[],
    fileType: string,
    successMessage: string,
    data: any,
    specialKeyData?:{},
    storageKey?: string
  ) {
    const safeRows = Array.isArray(data) ? data : [];
    let finalHeaders = headers;
    let finalColumns = columns;
    if (storageKey) {
      const filtered = filterDownloadColumnsByVisibility(
        this.localStorageService,
        storageKey,
        headers,
        columns
      );
      finalHeaders = filtered.headers;
      finalColumns = filtered.columnKeys;
    }
    const excelData: IExcelTableData = {
      name: name,
      headers: finalHeaders,
      headerKeys: [...finalColumns],
      tableData: safeRows,
      selectedFileType: fileType,
      successMessage: successMessage,
      isFromSummary: true,
    };
    this.fileService.generateExcelCSVFile(excelData,specialKeyData); 
  }

  public disableSpecificIcon(
    icons: SvgIconProp[],
    targetIconName: string
  ): SvgIconProp[] {
    const clonedIcons = JSON.parse(JSON.stringify(icons)) as SvgIconProp[];

    return clonedIcons.map((icon) => {
      if (icon.name === targetIconName) {
        icon.isDisabled = true;
      }
      return icon;
    });
  }
}
